import { type Component, createApp } from "vue";
import { assert } from "@utils/assertion";
import { setupGaContext } from "@utils/vue-migration/common/gaContext/gaContext";
import { type GaContextDataSync, parseGaContextDataSync } from "@generated/model/gaContextDataSync";
import { createRouter, createWebHistory } from "vue-router";
import { getGaDesignTranslations } from "@/common/i18nUtils";
import { createContext } from "@utils/widgetContextUtils";
import { LanguageHelper } from "@utils/type/type";
import { parseJsonWithNumbersAsBigint } from "@utils/json";
import { fetchRoleResources } from "@utils/roleService";

type Module = (rootElement: Element) => void;

const modules: { callback: Module; selector: string }[] = [];
const widgets = new Map<string, () => Promise<Component>>();
const alreadyInitializedElements = new Set<Element>();
const initFunctions = new Set<() => void>();

export function registerModule(selector: string, callback: Module): void {
  modules.push({ selector, callback });
}

export function updateDom(rootElement: Document | Element): void {
  modules.forEach((module) => {
    rootElement.querySelectorAll(module.selector).forEach((domElement) => {
      if (!alreadyInitializedElements.has(domElement)) {
        alreadyInitializedElements.add(domElement);
        module.callback(domElement);
      }
    });
  });
}

export function gaInitGeneral(rootElement: Document | Element): void {
  /* eslint-disable-next-line */
  (window as unknown as any).$(rootElement).gaInitGeneral();
}

document.addEventListener("ga.dom.updated", function (event) {
  if (event.target instanceof Element) {
    updateDom(event.target);
  } else {
    updateDom(document);
  }
});

export function registerVueWidgetForJsp(name: string, component: () => Promise<Component>): void {
  widgets.set(name, component);
}

export function registerGlobalInitFunction(fn: () => void): void {
  initFunctions.add(fn);
}

// Our scripts load with `defer`, so per spec DOMContentLoaded only fires after they finish running -
// but if it has already fired by the time this executes, a bare addEventListener would never call
// init(), so check readyState and run immediately in that case instead.
async function init(): Promise<void> {
  if (globalThis.IS_PROSPECT || globalThis.IS_UNIT_TEST) {
    return;
  }

  /* eslint-disable-next-line */
  const gaContextDataSync = parseGaContextDataSync((globalThis as any).gaContext);
  await Promise.all([
    setupGaContext(gaContextDataSync, {
      loadTranslations: getGaDesignTranslations,
    }),
    fetchRoleResources(),
  ]);

  initFunctions.forEach((fn) => fn());
  updateDom(document);
  if (widgets.size > 0) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).vue = {
      mountWidget: async (
        name: string,
        selector: string,
        opt: { widgetProps: unknown; generalProps: { language: string } } = { widgetProps: {}, generalProps: { language: "" } },
        context: string[],
        data: GaContextDataSync,
      ): Promise<void> => {
        const component = widgets.get(name);
        assert(component !== undefined, `Component '${name}' has not been registered`);

        const {
          widgetProps,
          generalProps: { language },
        } = opt;

        const lang = LanguageHelper.parseLangByIsoCode(language);
        const ctx = await createContext(context, lang, data);
        const props = Object.assign({}, widgetProps, { ctx });

        if (name === "GaDesignSpa") {
          const { generatedRoutes } = await import("@generated-routes/designRoutes");
          const router = createRouter({
            history: createWebHistory(),
            routes: [
              {
                path: "/:lang?",
                component: { template: "<router-view />" },
                children: [...generatedRoutes.blank],
              },
            ],
          });
          router.afterEach((to) => {
            const title = to.meta.title;
            if (typeof title === "string" && title.length > 0) {
              document.title = `${title} | getAbstract`;
            } else {
              document.title = "getAbstract";
            }
          });
          createApp(await component(), props)
            .use(router)
            .mount(selector);
        } else if (name === "GaDesignSpaWebApp") {
          const { generatedRoutes } = await import("@generated-routes/designRoutes");
          const router = createRouter({
            history: createWebHistory(),
            routes: [
              {
                path: "/:lang?",
                component: { template: "<router-view />" },
                children: [...generatedRoutes.webapp],
              },
            ],
          });
          router.afterEach((to) => {
            const title = to.meta.title;
            if (typeof title === "string" && title.length > 0) {
              document.title = `${title} | getAbstract`;
            } else {
              document.title = "getAbstract";
            }
          });
          createApp(await component(), props)
            .use(router)
            .mount(selector);
        } else {
          createApp(await component(), props).mount(selector);
        }
        //TODO setupSessionKeepalive
      },
    };

    document.dispatchEvent(new CustomEvent("ga.design.ready", { bubbles: true, detail: parseJsonWithNumbersAsBigint }));
  }
}

async function initSafely(): Promise<void> {
  try {
    await init();
  } catch (error) {
    console.error("gaDesign Registry init() failed", error);
  }
}

if (document.readyState === "loading") {
  addEventListener("DOMContentLoaded", () => void initSafely());
} else {
  void initSafely();
}
